import { useEffect, useRef, useState } from "react";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { useSearchParams } from "react-router-dom";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import SalaryAdvanceModalForm from "../modals/SalaryAdvanceModalForm";
import SalaryAdvanceGroupModalForm from "../modals/SalaryAdvanceGroupModalForm";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { SalaryAdvanceDto } from "../../../../models/salary-advance.dto";
import {
	useDeleteSalaryAdvanceGroup,
	useGetSalaryAdvanceGroup,
} from "../../../../services/salary-advance-group.service";
import {
	useDeleteSalaryAdvance,
	useGetSalaryAdvance,
} from "../../../../services/salary-advance.service";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import UploadSalaryAdvances from "../modals/UploadSalaryAdvance";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	salaryAdvanceGroupColumns,
	salaryAdvanceGroupColumnVisibility,
	salaryAdvanceColumns,
	salaryAdvanceColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportSalaryAdvanceModal from "../modals/ExportSalaryAdvanceModal";

const SalaryAdvanceView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<
		"salaryAdvanceGroups" | "salaryAdvances"
	>("salaryAdvanceGroups");

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openSalaryAdvanceGroupModal, setOpenSalaryAdvanceGroupModal] =
		useState(false);
	const [openSalaryAdvanceModal, setOpenSalaryAdvanceModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openUploadSalaryAdvancesModal, setOpenUploadSalaryAdvancesModal] =
		useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Salary Advance Groups
	const {
		columnVisibilityModel: salaryAdvanceGroupColumnVisibilityModel,
		setColumnVisibilityModel: setSalaryAdvanceGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(salaryAdvanceGroupColumnVisibility);

	// Column visibility for Salary Advances
	const {
		columnVisibilityModel: salaryAdvanceColumnVisibilityModel,
		setColumnVisibilityModel: setSalaryAdvanceColumnVisibilityModel,
	} = useColumnVisibilityModel(salaryAdvanceColumnVisibility);

	// Salary Advance Groups table
	const {
		tableAPIRef: salaryAdvanceGroupTableAPIRef,
		tableData: salaryAdvanceGroupTableData,
		isLoading: salaryAdvanceGroupLoading,
		pageCount: salaryAdvanceGroupPageCount,
	} = useManageTableWithSearchParams<SalaryAdvanceDto>({
		fetchHook: (options) =>
			useGetSalaryAdvanceGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Salary Advances table
	const {
		tableAPIRef: salaryAdvanceTableAPIRef,
		tableData: salaryAdvanceTableData,
		isLoading: salaryAdvanceLoading,
		pageCount: salaryAdvancePageCount,
	} = useManageTableWithSearchParams<SalaryAdvanceDto>({
		fetchHook: useGetSalaryAdvance,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: salaryAdvanceGroups } = useGetSalaryAdvanceGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenSalaryAdvanceGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenSalaryAdvanceGroupModal(true);
	};

	const handleOpenSalaryAdvanceModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenSalaryAdvanceModal(true);
	};

	const handleTabChange = (tab: "salaryAdvanceGroups" | "salaryAdvances") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteSalaryAdvanceGroup } = useDeleteSalaryAdvanceGroup();
	const { mutate: deleteSalaryAdvance } = useDeleteSalaryAdvance();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "salaryAdvanceGroups") {
			deleteSalaryAdvanceGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteSalaryAdvance(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOpenExportModal = () => {
		setIsUploadExportOpen(false);
		setOpenExportModal(true);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-2 mb-3">
				<div className="flex items-center gap-3">
					<TableFilter
						fields={
							activeTab === "salaryAdvanceGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Start Pay Period",
											fieldName: "startPayPeriodId",
											fieldType: "SELECT_FIELD",
											options: payPeriods?.data || [],
											mapText: "code",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Employee",
											fieldName: "employeeId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Start Pay Period",
											fieldName: "startPayPeriodId",
											fieldType: "SELECT_FIELD",
											options: payPeriods?.data || [],
											mapText: "code",
										},
										{
											title: "Salary Advance Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "salaryAdvanceGroups"
								? (salaryAdvanceGroupColumns as any)
								: (salaryAdvanceColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "salaryAdvanceGroups"
								? salaryAdvanceGroupColumnVisibility
								: salaryAdvanceColumnVisibility
						}
						visibilityModel={
							activeTab === "salaryAdvanceGroups"
								? salaryAdvanceGroupColumnVisibilityModel
								: salaryAdvanceColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "salaryAdvanceGroups"
								? setSalaryAdvanceGroupColumnVisibilityModel
								: setSalaryAdvanceColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "salaryAdvanceGroups"
								? salaryAdvanceGroupTableAPIRef
								: salaryAdvanceTableAPIRef
						}
					/>
				</div>

				<div className="flex items-center gap-4 font-medium">
					{activeTab === "salaryAdvanceGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadSalaryAdvancesModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Salary Advance
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "salaryAdvances" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Salary Advance
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.SALARY_WRITE]}
					>
						{activeTab === "salaryAdvanceGroups" ? (
							<button
								onClick={handleOpenSalaryAdvanceGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Salary Advance Group
							</button>
						) : (
							<button
								onClick={handleOpenSalaryAdvanceModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Salary Advance
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-4">
				<button
					onClick={() => handleTabChange("salaryAdvanceGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "salaryAdvanceGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Salary Advance Groups
				</button>
				<button
					onClick={() => handleTabChange("salaryAdvances")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "salaryAdvances"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Salary Advances
				</button>
			</div>

			{activeTab === "salaryAdvanceGroups" ? (
				<TanStackEmployeeTable
					ref={salaryAdvanceGroupTableAPIRef}
					tableId={TableId.SALARY_ADVANCE_GROUPS}
					data={salaryAdvanceGroupTableData}
					columns={salaryAdvanceGroupColumns}
					columnVisibility={salaryAdvanceGroupColumnVisibilityModel}
					onColumnVisibilityChange={setSalaryAdvanceGroupColumnVisibilityModel}
					loading={salaryAdvanceGroupLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.SALARY_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={salaryAdvanceGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.SALARY_READ])}
					pageCount={salaryAdvanceGroupPageCount}
					apiRef={salaryAdvanceGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={salaryAdvanceTableAPIRef}
					tableId={TableId.SALARY_ADVANCE}
					data={salaryAdvanceTableData}
					columns={salaryAdvanceColumns}
					columnVisibility={salaryAdvanceColumnVisibilityModel}
					onColumnVisibilityChange={setSalaryAdvanceColumnVisibilityModel}
					loading={salaryAdvanceLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.SALARY_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={salaryAdvanceColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.SALARY_READ])}
					pageCount={salaryAdvancePageCount}
					apiRef={salaryAdvanceTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "salaryAdvanceGroups"
						? setOpenSalaryAdvanceGroupModal
						: setOpenSalaryAdvanceModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>

			{openSalaryAdvanceGroupModal && (
				<SalaryAdvanceGroupModalForm
					open={openSalaryAdvanceGroupModal}
					setOpen={setOpenSalaryAdvanceGroupModal}
					title={
						isEditForm
							? "Update Salary Advance Group"
							: "Create Salary Advance Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={salaryAdvanceGroupTableAPIRef}
					payPeriods={payPeriods?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openSalaryAdvanceModal && (
				<SalaryAdvanceModalForm
					open={openSalaryAdvanceModal}
					setOpen={setOpenSalaryAdvanceModal}
					title={isEditForm ? "Update Salary Advance" : "Create Salary Advance"}
					isEditForm={isEditForm}
					tableAPIRef={salaryAdvanceTableAPIRef}
					payPeriods={payPeriods?.data || []}
					employees={employees?.data || []}
					salaryAdvanceGroups={salaryAdvanceGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadSalaryAdvancesModal && (
				<UploadSalaryAdvances
					open={openUploadSalaryAdvancesModal}
					setOpen={setOpenUploadSalaryAdvancesModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportSalaryAdvanceModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default SalaryAdvanceView;
